import { useRef } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useTheme } from '@context/ThemeContext'
import { useSound } from '@context/SoundContext'
import { useAnimations } from '@context/AnimationsContext'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { useEscapeKey } from '@hooks/useEscapeKey'
import { useAutoFocus } from '@hooks/useAutoFocus'
import { cx } from '@utils/cx'
import LanguageSelector from '../selectors/LanguageSelector'
import ThemeSelector from '../selectors/ThemeSelector'
import { CloseIcon, LanguageIcon, SunIcon, MoonIcon, MotionIcon, MotionOffIcon, SoundIcon, SoundOffIcon } from '../Icons'
import styles from './SettingsModal.module.css'
import baseStyles from '@styles/modalBase.module.css'

export default function SettingsModal ({ isOpen, onClose }) {
  const { t } = useLanguage()
  const { theme } = useTheme()
  const { soundEnabled, toggleSound } = useSound()
  const { animationsEnabled, toggleAnimations } = useAnimations()
  const modalRef = useRef(null)
  const messageRef = useRef(null)

  useFocusTrap(modalRef)
  useEscapeKey(onClose, isOpen)
  useAutoFocus(messageRef, isOpen)

  if (!isOpen) return null

  return (
    <div
      className={baseStyles.overlayBottom}
      role='dialog'
      aria-modal='true'
      aria-labelledby='settings-title'
    >
      <div className={styles.settingsmodal} ref={modalRef}>
        <div className={baseStyles.bottomHandle} />

        <header className={styles.header}>
          <h2
            id='settings-title'
            className={styles.title}
            ref={messageRef}
            tabIndex='-1'
          >
            {t.core.preferences.title}
          </h2>
          <button
            onClick={onClose}
            className={styles.close}
            aria-label={t.core.access.preferences.close}
            title={t.core.access.preferences.close}
          >
            <CloseIcon size={20} stroke={1.5} aria-hidden='true' />
            <span className='sr-only'>{t.core.access.preferences.close}</span>
          </button>
        </header>

        <div className={styles.content}>

          {/* Language Setting */}
          <div className={styles.item}>
            <div className={styles.itemHeader}>
              <LanguageIcon size={18} stroke={1.5} aria-hidden='true' />
              <span className={styles.itemLabel}>{t.core.preferences.language}</span>
            </div>
            <div className={styles.itemControl}>
              <LanguageSelector />
            </div>
          </div>

          {/* Theme Setting */}
          <div className={styles.item}>
            <div className={styles.itemHeader}>
              {theme === 'light'
                ? (<SunIcon size={18} stroke={1.5} aria-hidden='true' />)
                : (<MoonIcon size={18} stroke={1.5} aria-hidden='true' />)}
              <span className={styles.itemLabel}>{t.core.preferences.theme}</span>
            </div>
            <div className={styles.itemControl}>
              <ThemeSelector />
            </div>
          </div>

          {/* Sound Setting */}
          <div className={styles.item}>
            <div className={styles.itemHeader}>
              {soundEnabled
                ? <SoundIcon size={18} stroke={1.5} aria-hidden='true' />
                : <SoundOffIcon size={18} stroke={1.5} aria-hidden='true' />}
              <span className={styles.itemLabel}>{t.core.preferences.sound}</span>
            </div>
            <div className={styles.itemControl}>
              <button
                onClick={toggleSound}
                className={cx(
                  styles.toggleSlider,
                  soundEnabled && styles.toggleSliderActive
                )}
                aria-label={t.core.access.preferences.soundToggle}
                role='switch'
                aria-checked={soundEnabled}
              />
            </div>
          </div>

          {/* Animations setting */}
          <div className={styles.item}>
            <div className={styles.itemHeader}>
              {animationsEnabled
                ? <MotionIcon size={18} stroke={1.5} aria-hidden='true' />
                : <MotionOffIcon size={18} stroke={1.5} aria-hidden='true' />}
              <span className={styles.itemLabel}>{t.core.preferences.animations}</span>
            </div>
            <div className={styles.itemControl}>
              <button
                onClick={toggleAnimations}
                className={cx(
                  styles.toggleSlider,
                  animationsEnabled && styles.toggleSliderActive
                )}
                aria-label={t.core.access.preferences.animationsToggle}
                role='switch'
                aria-checked={animationsEnabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
